import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { SearchFilterBar, TabStrip, TopicFilter } from '@shared-ui/components';
import { QuestionSearchFilter, type QuestionSearchOption } from '@shared-ui/question-picker';
import { useModules, useSubjectLabel } from '@shared-ui/reference-data';
import { questionQualityApi, LEDGER_QUERY_KEY } from '../../../api/questionQualityApi';
import type { FilterOptions } from '../../../api/questionQualityApi';
import { ByRule } from './ByRule';
import { ByQuestion } from './ByQuestion';
import { buildFilterDefs, useQualityFilters } from './useQualityFilters';
import './QuestionQuality.css';

/**
 * Monitoring → Question Quality. The LLM-judged sibling of the Diagnosis tab:
 * the standing quality ledger (`question-quality-checks`), read two ways —
 * BY RULE (which check is our weakest?) and BY QUESTION (which questions are
 * broken, and on how many checks?). Verdicts are produced by the offline
 * scripts/quality tooling. See docs/FeatureDesigns/question-quality/checks-target.md.
 *
 * **One fetch, one filter bar, two projections.** The views used to own a fetch
 * and a bar each, over endpoints that answered with DIFFERENT populations — so a
 * filter could be applied to one and had no meaning for the other, and the two
 * could disagree about the same corpus. The host reads the ledger once and
 * narrows it once; By Rule counts the survivors per rule, By Question lists them.
 * That is also why the bar sits ABOVE the tab strip: it is not a view's filter,
 * it is the page's.
 */

type QualityView = 'by-rule' | 'by-question';

/** Committed on pick rather than on Fetch — see the bar below. Module-level so
 *  it is not a new array on every render. */
const SCOPE_KEYS = ['subject'];

const VIEWS = [
    { key: 'by-rule' as const, label: 'By Rule' },
    { key: 'by-question' as const, label: 'By Question' },
];

export function QuestionQuality() {
    const [searchParams, setSearchParams] = useSearchParams();
    const view: QualityView = searchParams.get('qq') === 'by-question' ? 'by-question' : 'by-rule';
    const { subject, setSubject, filters, setFilters, page, setPage } = useQualityFilters();
    const subjectLabel = useSubjectLabel();
    const { getModulesForSubject } = useModules();
    const topicModules = useMemo(() => getModulesForSubject(subject), [getModulesForSubject, subject]);

    const [options, setOptions] = useState<FilterOptions | null>(null);

    /**
     * One read per (subject, filters, page) — the server filters and pages now.
     *
     * `filters` is serialised into the key rather than passed as an object so a
     * re-render with an equal-but-new object does not refetch.
     *
     * `useQuery`, not a plain fetch-on-mount: this tab unmounts every time the
     * reader opens a finding (a route change to the findings page) and remounts
     * on the way back. A plain effect would reload the whole ledger on every
     * such round trip — the thing this was slow for. Caching the read means
     * the list is back INSTANTLY, and the findings page patches just the one
     * row it touched (`patchLedgerQuestionResolution`) rather than the cache
     * going stale and forcing a reload to show it.
     */
    const filterKey = JSON.stringify(filters);
    const { data, isFetching: loading, error: queryError } = useQuery({
        queryKey: [LEDGER_QUERY_KEY, subject, filterKey, page, view],
        queryFn: () => questionQualityApi.ledger(subject, {
            page,
            filters: {
                ...(JSON.parse(filterKey) as Record<string, string>),
                // By Question lists triage; By Rule scores every checked
                // question, clean ones included.
                ...(view === 'by-question' ? { flaggedOnly: '1' } : {}),
            },
        }),
        staleTime: 5 * 60_000,
        // Defaults to 5 minutes, and the clock starts when the reader opens a
        // finding — so the entry was collected before they could come back to it.
        gcTime: 30 * 60_000,
        // Keeps the previous filter/page's rows on screen while a NEW
        // combination loads, instead of a "Loading…" flash — orthogonal to the
        // cache hit on `[back]`, which needs no fetch at all.
        placeholderData: keepPreviousData,
    });
    const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to load the quality ledger') : null;

    // Options describe the SUBJECT, so they are fetched once per subject and not
    // per filter change — narrowing one filter must not empty the others.
    useEffect(() => {
        let live = true;
        questionQualityApi.filterOptions(subject)
            .then(o => { if (live) setOptions(o); })
            .catch(() => { if (live) setOptions(null); });
        return () => { live = false; };
    }, [subject]);

    const rules = useMemo(() => data?.rules ?? [], [data]);
    /** The page, already filtered by the server. */
    const rows = useMemo(() => data?.questions ?? [], [data]);

    /** Offered before anything is typed, and how a restored `?question=` value
     *  gets its text back without a search — deduped in row order. */
    const questionSuggestions = useMemo<QuestionSearchOption[]>(() => {
        const seen = new Set<string>();
        const out: QuestionSearchOption[] = [];
        for (const r of rows) {
            if (seen.has(r.questionId)) continue;
            seen.add(r.questionId);
            out.push({ questionId: r.questionId, questionText: r.questionText });
        }
        return out;
    }, [rows]);

    const setView = useCallback((next: QualityView) => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.set('qq', next);
            return params;
        }, { replace: true });
    }, [setSearchParams]);

    const filterDefs = useMemo(() => buildFilterDefs({
        rules,
        options,
        subjectLabel,
    }), [rules, options, subjectLabel]);

    return (
        <div className="settings-section">
            <div className="section-header">
                <h2>Question Quality</h2>
            </div>

            {/* Above the tabs, because it means the same thing on both sides of
                them — see the note at the top of this file. */}
            <SearchFilterBar
                filters={filterDefs}
                values={{ ...filters, subject }}
                onChange={next => {
                    if (next.subject !== subject) setSubject(next.subject);
                    else setFilters(next);
                }}
                hideSearch
                loading={loading}
                disableFetchWhenClean
                // Subject is the page's SCOPE, not one of its filters: the topic
                // list, the check list and the question search are all read from
                // it. Behind Fetch it read as a lie — the select said Physics
                // while the topic placeholder said "Any topic in Chemistry" and
                // the question search offered chemistry questions, because the
                // committed subject was still chemistry. It now commits on pick,
                // like the two controls it governs.
                immediateKeys={SCOPE_KEYS}
                // Topic sits right after Subject and commits immediately, like
                // every other module-scoped picker (see PastActivity.tsx) — a
                // topic pick is a deliberate navigation, not a draft edit to
                // queue up behind Fetch.
                renderAfter={{
                    subject: (
                        <TopicFilter
                            subject={subject}
                            modules={topicModules}
                            value={filters.topic ?? ''}
                            onChange={topic => setFilters({ ...filters, topic })}
                            placeholder={subject ? `Any topic in ${subjectLabel(subject)}` : 'Any topic'}
                        />
                    ),
                }}
            >
                {/* Also commits immediately, like Topic — a pick is a navigation,
                    not a draft to queue behind Fetch (see AnalysisList.tsx). */}
                <QuestionSearchFilter
                    includeOwned
                    value={filters.question ?? ''}
                    onChange={question => setFilters({ ...filters, question })}
                    suggestions={questionSuggestions}
                    suggestionsLabel="Questions on this page"
                    valueText={questionSuggestions.find(o => o.questionId === filters.question)?.questionText}
                    subject={subject}
                    placeholder="Search question…"
                />
            </SearchFilterBar>

            <TabStrip
                tabs={VIEWS}
                active={view}
                onChange={setView}
                ariaLabel="Question quality views"
                className="qq-views"
            />

            {error && <p className="qq-warn">{error}</p>}
            {loading && !data && <p className="qq-dim">Loading…</p>}

            {/* "Nothing matched your filters" and "nothing has ever been judged
                here" are different answers and used to print the same one. */}
            {data && data.total === 0 && !loading && (
                <p className="qq-dim">
                    {Object.values(filters).some(Boolean)
                        ? <>No question in <b>{subjectLabel(subject)}</b> matches these filters.</>
                        : <>No verdicts recorded for <b>{subjectLabel(subject)}</b> yet. Run the
                            worklist → judge → record flow (<code>scripts/quality</code>) to populate this.</>}
                </p>
            )}

            {data && data.total > 0 && (
                view === 'by-rule'
                    ? <ByRule counts={data.ruleCounts} totals={data.totals} poolTotal={data.poolTotal}
                        ruleInfo={data.ruleInfo} subject={subject} filters={filters} />
                    : (
                        <ByQuestion
                            rows={rows}
                            rules={rules}
                            subject={subject}
                            page={page}
                            pageSize={data.pageSize}
                            onPageChange={setPage}
                            truncated={data.truncated}
                            total={data.total}
                        />
                    )
            )}
        </div>
    );
}
